import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Container } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import "./LandingPage.css";

const FEATURES = [
  {
    icon: "bi-broadcast",
    title: "Live scoring",
    description:
      "Track goals, assists, and penalties in real time from any device via a secure scorekeeper link.",
  },
  {
    icon: "bi-bar-chart-line",
    title: "Standings & stats",
    description:
      "Automatic points calculation, per-player stats, and public-facing standings pages your fans can bookmark.",
  },
  {
    icon: "bi-people",
    title: "Roster management",
    description:
      "Manage teams and players across seasons. Player profiles carry stats forward year over year.",
  },
  {
    icon: "bi-calendar3",
    title: "Schedules",
    description:
      "Build and publish your season schedule. Results populate automatically as games are scored.",
  },
  {
    icon: "bi-shield-check",
    title: "Role-based access",
    description:
      "Owners, admins, and scorekeepers each get the right level of access — nothing more, nothing less.",
  },
  {
    icon: "bi-globe",
    title: "Public league pages",
    description:
      "Share standings, schedules, and player stats with anyone — no login required for fans.",
  },
] as const;

const STATS = [
  { value: "15+", label: "Years of stats" },
  { value: "10,000+", label: "Goals recorded" },
  { value: "200+", label: "Seasons tracked" },
  { value: "3,000+", label: "Registered players" },
] as const;

export default function LandingPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSetUpLeague = () => {
    if (user) {
      navigate("/setup");
    } else {
      navigate("/register", { state: { intent: "create-league" } });
    }
  };

  return (
    <>
      <main>
        <section className="landing-hero">
          <Container className="landing-hero-inner">
            <div className="landing-eyebrow">
              <i className="bi bi-trophy" aria-hidden="true" />
              Hockey league management
            </div>
            <h1 className="landing-headline">
              Run your league.
              <br />
              <span className="landing-headline-accent">
                From first puck drop
              </span>
              <br />
              to final standings.
            </h1>
            <p className="landing-subheadline">
              Podium handles scoring, stats, rosters, and schedules — so you can
              focus on the game.
            </p>
            <div className="landing-ctas">
              {user ? (
                <Button variant="primary" size="lg" onClick={handleSetUpLeague}>
                  Set up a league
                </Button>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-lg">
                    Create a free account
                  </Link>
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    onClick={handleSetUpLeague}
                  >
                    Set up a league
                  </Button>
                </>
              )}
            </div>
            {!user && (
              <p className="landing-cta-footnote">
                No credit card required&nbsp;·&nbsp;Free to start
              </p>
            )}
          </Container>
        </section>

        <section
          className="landing-score-demo"
          aria-label="Sample live game score"
        >
          <Container>
            <div className="score-demo-card">
              <div className="score-demo-header">
                <span className="score-demo-meta">
                  Friday, Jan 12&nbsp;·&nbsp;3rd Period
                </span>
                <span className="score-demo-live">
                  <span className="score-demo-live-dot" aria-hidden="true" />
                  Live
                </span>
              </div>
              <div className="score-demo-body">
                <div className="score-demo-row">
                  <div className="score-demo-team">
                    <span className="score-demo-team-name">
                      Northside Wolves
                    </span>
                    <span className="score-demo-team-record">
                      12W · 4L · 1T
                    </span>
                  </div>
                  <span className="score-demo-num">3</span>
                </div>
                <div className="score-demo-row">
                  <div className="score-demo-team">
                    <span className="score-demo-team-name">
                      Downtown Lightning
                    </span>
                    <span className="score-demo-team-record">9W · 6L · 2T</span>
                  </div>
                  <span className="score-demo-num">2</span>
                </div>
              </div>
              <div className="score-demo-footer">
                <span>P. Kowalski (14:22 · 3rd)</span>
                <span className="score-demo-clock">14:22 remaining</span>
              </div>
            </div>
          </Container>
        </section>

        <section className="landing-stats" aria-label="Platform statistics">
          <Container>
            <ul className="landing-stats-list">
              {STATS.map(({ value, label }) => (
                <li key={label} className="landing-stat">
                  <span className="landing-stat-value">{value}</span>
                  <span className="landing-stat-label">{label}</span>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="landing-features">
          <Container>
            <p className="landing-section-label">
              Everything your league needs
            </p>
            <div className="landing-features-grid">
              {FEATURES.map(({ icon, title, description }) => (
                <div key={title} className="landing-feature-card">
                  <div className="landing-feature-icon" aria-hidden="true">
                    <i className={`bi ${icon}`} />
                  </div>
                  <h3 className="landing-feature-title">{title}</h3>
                  <p className="landing-feature-desc">{description}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section className="landing-bottom-cta">
          <Container className="landing-bottom-cta-inner">
            <h2 className="landing-bottom-cta-heading">
              Ready to run your league?
            </h2>
            <p className="landing-bottom-cta-sub">
              Trusted by hockey leagues since 2009. Get set up in minutes.
            </p>
            <div className="landing-ctas">
              {user ? (
                <Button variant="primary" size="lg" onClick={handleSetUpLeague}>
                  Set up a league
                </Button>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-lg">
                    Create a free account
                  </Link>
                  <Link
                    to="/login"
                    className="btn btn-outline-secondary btn-lg"
                  >
                    Log in
                  </Link>
                </>
              )}
            </div>
          </Container>
        </section>
      </main>
    </>
  );
}
